// First-touch signup attribution (openspec 111, D6). Captured on the first
// page view that has no stored value, sent once after sign-in, then deleted.
// No cookies and no third parties; the referrer keeps only origin + path.

const STORAGE_KEY = 'uptrack_attribution'
const MAX_LEN = 200

export type Attribution = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  referrer?: string
  landing_path?: string
}

const clip = (v: string | null | undefined) => (v ? v.trim().slice(0, MAX_LEN) || undefined : undefined)

function externalReferrer(): string | undefined {
  try {
    if (!document.referrer) return undefined
    const ref = new URL(document.referrer)
    if (ref.origin === window.location.origin) return undefined
    return clip(`${ref.origin}${ref.pathname}`)
  } catch {
    return undefined
  }
}

// Signed-in app routes are not acquisition landings; capturing there would
// attribute an existing user's dashboard visit as a signup source.
const APP_PATH = /^\/(dashboard|checkout|admin|auth|invitations)(\/|$)/

export function captureAttribution() {
  try {
    if (APP_PATH.test(window.location.pathname)) return
    if (localStorage.getItem(STORAGE_KEY)) return
    const params = new URLSearchParams(window.location.search)
    const data: Attribution = {
      utm_source: clip(params.get('utm_source')),
      utm_medium: clip(params.get('utm_medium')),
      utm_campaign: clip(params.get('utm_campaign')),
      referrer: externalReferrer(),
      landing_path: clip(window.location.pathname),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // Storage unavailable: attribution is optional.
  }
}

/** Send the stored attribution once; the key is removed only after success. */
export async function flushAttribution(send: (data: Attribution) => Promise<unknown>) {
  let data: Attribution
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    data = JSON.parse(raw) as Attribution
  } catch {
    return
  }
  try {
    await send(data)
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Keep it for the next authenticated load.
  }
}
